<script lang="ts">
    import { DEFAULT_LAYOUT } from '$lib/components/constants';

    import { getAllTrackingProperties } from '$lib/helper';
    import { isUrl } from '$lib/utils/isUrl';

    import { onMount, onDestroy } from 'svelte';

    import { CalendarProvider } from './constants';
    import Form from './FormQuestion.View.svelte';
    import { verifyOtpForSubmit } from './verifyOtpForSubmit';
    import { cal } from '../_utils/cal';
    import { CAL_EVENT_SCHEDULED, CAL_EVENT_SCHEDULED_V2 } from '../_utils/constants';

    import type { ButtonStyle } from '$lib/types/Components/Button/ButtonContent';
    import type { InputData } from '$lib/types/Components/FormQuestion/InputData';
    import type { Box as BoxType, Align, Size } from '$lib/types/Components/SharedTypes';
    import type { FormValues } from '$lib/types/Form';
    import type { Language } from '$lib/types/Language';
    import type { OtpVerificationParams } from '$lib/types/OtpVerification';
    import type { Theme, TransitionEffect } from '$lib/types/Theme';

    export let campaignId = '';

    // Cal.com
    export let calendarUrl = '';
    export let provider = undefined;
    export let onCalFailed = () => console.error('Cal.com could not be loaded...');
    export let onCalendarFormSubmit: (formValues: FormValues) => void = () => null;
    export let otpVerificationParams: OtpVerificationParams | null = null;

    // Question
    export let id = '';
    export let theme: Theme;
    export let align: Align = 'center';
    export let wysiwygHtml: string | undefined = undefined;
    export let color = 'black';
    export let box: BoxType;
    export let onSubmit: (formValues: FormValues) => void = () => null;
    export let onMouseEnter = () => null;
    export let layout = DEFAULT_LAYOUT;
    export let language: Language = 'en';
    export let transitionDelay = 0;
    export let transitionEffect: TransitionEffect = 'default';

    // Inputs
    export let inputs: InputData[] = [];

    // Submit Button
    export let btnId = '';
    export let btnWysiwygHtml: string | undefined = undefined;
    export let btnBgColor = '#0076FF';
    export let btnColor = 'white';
    export let btnAlign: Align = 'center';
    export let btnFullWidthOnDesktop: boolean = false;
    export let btnSize: Size = 'medium';
    export let btnStyle: ButtonStyle = 'filled';
    export let loading = false;

    let savedFormValues: FormValues = {};

    const isValidUrl =
        provider === CalendarProvider.CAL ? isUrl(calendarUrl) : cal.validateUrl(calendarUrl);

    onMount(() => {
        window.addEventListener('message', handleMessageEvent);
        // Detect and use the appropriate Cal.com domain (.com or .eu)
        // Custom domains always default to 'com'
        const domain = cal.getCalDomain(calendarUrl);
        // Detect organization subdomain (e.g., 'test' from 'test.cal.com')
        const orgSubdomain = cal.getOrgSubdomain(calendarUrl);
        const customDomain = orgSubdomain ? null : cal.getCustomDomain(calendarUrl);

        cal.enqueueScripts(domain, orgSubdomain, customDomain);
    });

    onDestroy(() => {
        window.removeEventListener('message', handleMessageEvent);

        if (window.Cal) {
            cal.closeModal();
        }
    });

    const openCalPopup = (formData: FormValues) => {
        const Cal = window.Cal;

        if (!Cal) {
            onCalFailed();
            return;
        }

        const allTrackingProperties = getAllTrackingProperties(campaignId);

        const namedAnswers = cal.extractNamedAnswers(inputs, formData, allTrackingProperties);
        const calConfig = cal.buildConfig(calendarUrl, namedAnswers, allTrackingProperties);

        Cal('modal', calConfig);
    };

    const handleEventScheduled = () => {
        cal.closeModal();

        onSubmit(savedFormValues);
    };

    const isCalBookingSuccessful = (data: { originator: string; type: string }): boolean => {
        return (
            data.originator === 'CAL' &&
            (data.type === CAL_EVENT_SCHEDULED || data.type === CAL_EVENT_SCHEDULED_V2)
        );
    };

    const handleMessageEvent = (event: MessageEvent) => {
        const data = event.data;

        if (!data || typeof data !== 'object') return;

        // Check for booking successful event
        if (isCalBookingSuccessful(data)) {
            handleEventScheduled();
        }
    };

    // FUN-3876: Retry hint shown below the submit button when verification
    // could not be started and the submit fails closed
    let otpSubmitError: string | null = null;

    const handleSubmit = async (formData: FormValues) => {
        otpSubmitError = null;

        // FUN-3876: Verify before any tracking or navigation — also for the
        // invalid-URL fallback, which would otherwise bypass the OTP gate
        const verification = await verifyOtpForSubmit(
            otpVerificationParams,
            formData,
            language,
            'Cal.com',
        );

        if (!verification.ok) {
            otpSubmitError = verification.errorMessage;
            return;
        }

        // FUN-3876: Track the form submit only after successful verification
        onCalendarFormSubmit(formData);

        // Open Cal.com popup
        if (isValidUrl) {
            savedFormValues = formData;

            return openCalPopup(formData);
        }

        // Navigate to next page without Cal.com popup
        return onSubmit(formData);
    };
</script>

<Form
    {id}
    {theme}
    {align}
    {wysiwygHtml}
    {color}
    {box}
    {inputs}
    {btnId}
    {btnWysiwygHtml}
    {btnBgColor}
    {btnFullWidthOnDesktop}
    {btnColor}
    {btnAlign}
    {btnStyle}
    {btnSize}
    {loading}
    {layout}
    {language}
    {onMouseEnter}
    {transitionEffect}
    {transitionDelay}
    onSubmit={handleSubmit}
    submitError={otpSubmitError}
/>
